import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AgencyProvider } from "./context/AgencyContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import DataQualityPage from "./pages/DataQualityPage";
import PatientsPage from "./pages/PatientsPage";
import ClaimsPage from "./pages/ClaimsPage";
import BillingHubPage from "./pages/BillingHubPage";
import OdmCredentialsPage from "./pages/OdmCredentialsPage";
import EligibilityBatchesPage from "./pages/EligibilityBatchesPage";
import DenialsPage from "./pages/DenialsPage";
import RhinobillClaimPage from "./pages/RhinobillClaimPage";
import TemplatesPage from "./pages/TemplatesPage";
import CodesPage from "./pages/CodesPage";
import EmployeesPage from "./pages/EmployeesPage";
import EVVPage from "./pages/EVVPage";
import DODDBillingPage from "./pages/DODDBillingPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import ODMConfigPage from "./pages/ODMConfigPage";
import BHBillingPage from "./pages/BHBillingPage";
import ReportsPage from "./pages/ReportsPage";
import UserManagementPage from "./pages/UserManagementPage";
import AuditLogPage from "./pages/AuditLogPage";
import RetentionConsolePage from "./pages/RetentionConsolePage";
import AgencyManagementPage from "./pages/AgencyManagementPage";
import AgencySettingsPage from "./pages/AgencySettingsPage";
import AZAIBillingPage from "./pages/AZAIBillingPage";
import StaffInvitationsPage from "./pages/StaffInvitationsPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import CrossAgencyReportsPage from "./pages/CrossAgencyReportsPage";
import EmailAuditPage from "./pages/EmailAuditPage";
import OdmCertDashboardPage from "./pages/OdmCertDashboardPage";
import OdmCertSupportEmailPage from "./pages/OdmCertSupportEmailPage";
import AgencyBillingHistoryPage from "./pages/AgencyBillingHistoryPage";
import EDISubmitPage from "./pages/EDISubmitPage";
import OdmSubmissionsPage from "./pages/OdmSubmissionsPage";
import OdmSubmissionDetailPage from "./pages/OdmSubmissionDetailPage";
import SandataCertPage from "./pages/SandataCertPage";
import EVVResolutionPage from "./pages/EVVResolutionPage";
import PayerContractsPage from "./pages/PayerContractsPage";
import AgencySignupPage from "./pages/AgencySignupPage";
import TimesheetScannerPage from "./pages/TimesheetScannerPage";
import AdminRecoveryPage from "./pages/AdminRecoveryPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MfaSettingsPage from "./pages/MfaSettingsPage";
import CertificationPage from "./pages/CertificationPage";
import CertificationRunnerPage from "./pages/CertificationRunnerPage";
import ElevationApprovalsPage from "./pages/ElevationApprovalsPage";
import ManualReviewPage from "./pages/ManualReviewPage";

// Layout
import Layout from "./components/Layout";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import IdleWarningDialog from "./components/security/IdleWarningDialog";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { showWarning, secondsLeft, staySignedIn } = useIdleTimeout();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Layout>
      {children}
      <IdleWarningDialog open={showWarning} secondsLeft={secondsLeft} onStay={staySignedIn} />
    </Layout>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  // Route registry — each entry is rendered with the appropriate wrapper.
  // Adding a new app section is a one-line addition here, not 8 lines of JSX.

  const PUBLIC_ROUTES = [
    ['/login', LoginPage],
    ['/register', RegisterPage],
    ['/signup', AgencySignupPage],
  ];

  // The vast majority of routes — agency users behind the standard
  // ProtectedRoute (auth check + Layout chrome).
  const PROTECTED_ROUTES = [
    ['/dashboard', DashboardPage],
    ['/patients', PatientsPage],
    // P36 (Feb 28, 2026): unified Billing Hub — single landing for the
    // billing section. The individual sub-pages remain reachable at
    // their existing URLs.
    ['/billing', BillingHubPage],
    ['/claims', ClaimsPage],
    ['/claims/denials', DenialsPage],
    ['/claims/new', RhinobillClaimPage],
    // Edit Draft claim. Same wizard component, prefilled with the
    // existing claim and saving via PUT instead of POST. Only Draft
    // claims should hit this route — submitted ones use Correct/Void.
    ['/claims/edit/:claimId', RhinobillClaimPage],
    ['/templates', TemplatesPage],
    ['/codes', CodesPage],
    ['/payer-contracts', PayerContractsPage],
    ['/employees', EmployeesPage],
    ['/evv', EVVPage],
    ['/dodd', DODDBillingPage],
    ['/integrations', IntegrationsPage],
    ['/odm-config', ODMConfigPage],
    ['/edi-submit', EDISubmitPage],
    ['/odm/submissions', OdmSubmissionsPage],
    ['/odm/submissions/:batchId', OdmSubmissionDetailPage],
    ['/sandata-cert', SandataCertPage],
    ['/evv/resolution', EVVResolutionPage],
    ['/timesheets', TimesheetScannerPage],
    ['/bh-billing', BHBillingPage],
    ['/reports', ReportsPage],
    ['/admin/users', UserManagementPage],
    ['/admin/audit', AuditLogPage],
    ['/admin/retention', RetentionConsolePage],
    ['/admin/agencies', AgencyManagementPage],
    ['/admin/agency-settings', AgencySettingsPage],
    ['/admin/billing', AZAIBillingPage],
    ['/admin/invitations', StaffInvitationsPage],
    ['/admin/cross-agency', CrossAgencyReportsPage],
    ['/admin/email-log', EmailAuditPage],
    ['/admin/odm-cert', OdmCertDashboardPage],
    ['/admin/odm-cert/support-email', OdmCertSupportEmailPage],
    ['/admin/odm-cert/270-package', CertificationPage],
    ['/admin/odm-cert/runner', CertificationRunnerPage],
    ['/admin/security/elevation-approvals', ElevationApprovalsPage],
    ['/admin/manual-review', ManualReviewPage],
    ['/admin/odm-credentials', OdmCredentialsPage],
    ['/admin/eligibility-batches', EligibilityBatchesPage],
    ['/billing/history', AgencyBillingHistoryPage],
    ['/data-quality', DataQualityPage],
    ['/account/mfa', MfaSettingsPage],
  ];

  // Bare routes that don't need auth or chrome (invitation links, password
  // reset flow, admin recovery break-glass).
  const BARE_ROUTES = [
    ['/accept-invite/:code', AcceptInvitePage],
    ['/admin-recovery', AdminRecoveryPage],
    ['/forgot-password', ForgotPasswordPage],
    ['/reset-password', ResetPasswordPage],
  ];

  return (
    <Routes>
      {PUBLIC_ROUTES.map(([path, Page]) => (
        <Route key={path} path={path} element={<PublicRoute><Page /></PublicRoute>} />
      ))}
      {PROTECTED_ROUTES.map(([path, Page]) => (
        <Route key={path} path={path} element={<ProtectedRoute><Page /></ProtectedRoute>} />
      ))}
      {BARE_ROUTES.map(([path, Page]) => (
        <Route key={path} path={path} element={<Page />} />
      ))}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AgencyProvider>
          <WebSocketProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </WebSocketProvider>
        </AgencyProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
